import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { FileEarmarkText } from 'react-bootstrap-icons';
import { Organization, getVesFiscalData } from '../../../models';
import { loadConfigurationFactors } from '../../../api/configuration';

interface FiscalDataBannerProps {
  organization: Organization | null;
}

/** R.I.F. format: one of V, J, G, E, P (case insensitive) then optional hyphen and digits */
function isRifValid(rif: string | undefined): boolean {
  if (!rif || typeof rif !== 'string') return false;
  const trimmed = rif.trim();
  if (trimmed.length < 8) return false;
  const letter = trimmed.charAt(0).toUpperCase();
  if (!['V', 'J', 'G', 'E', 'P'].includes(letter)) return false;
  const rest = trimmed.slice(1).replace(/-/g, '');
  return /^\d{7,10}$/.test(rest);
}

function isFiscalDataComplete(organization: Organization | null): boolean {
  const ves = getVesFiscalData(organization);
  if (!ves) return false;
  const rif = ves.rif ?? (organization?.taxId as string | undefined);
  if (!isRifValid(rif)) return false;
  if (!ves.taxpayerType) return false;
  if (!ves.fiscalAddress?.trim()) return false;
  if (!ves.fiscalState?.trim()) return false;
  if (!ves.fiscalMunicipality?.trim()) return false;
  return true;
}

const FiscalDataBanner: React.FC<FiscalDataBannerProps> = ({
  organization,
}) => {
  const navigate = useNavigate();
  const [currency, setCurrency] = useState<string | null>(null);

  useEffect(() => {
    if (!organization?.id) {
      setCurrency(null);
      return;
    }
    let cancelled = false;
    loadConfigurationFactors(organization.id)
      .then((config) => {
        if (!cancelled) setCurrency(config.currency ?? 'VES');
      })
      .catch(() => {
        if (!cancelled) setCurrency(null);
      });
    return () => {
      cancelled = true;
    };
  }, [organization?.id]);

  if (!organization?.id || currency !== 'VES') return null;
  if (isFiscalDataComplete(organization)) return null;

  const goToFiscalConfig = () => {
    navigate('/configuration?tab=fiscal');
  };

  return (
    <Alert
      variant="secondary"
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2 px-3 border"
    >
      <span className="small">
        <FileEarmarkText size={14} className="me-1 align-middle" />
        Tiene pendiente completar los <strong>Datos Maestros de Identificación</strong> (RIF,
        tipo de contribuyente, dirección fiscal con estado y municipio).
      </span>
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={goToFiscalConfig}
        className="text-nowrap"
      >
        Configurar
      </Button>
    </Alert>
  );
};

export default FiscalDataBanner;
